import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { useCallback, useMemo } from 'react';
import { CasinoGameRow } from '../../@types/casino';
import { QUERY_KEYS } from '../../constants';
import API_ROUTES from '../../constants/apiRoutes';
import { buildCasinoGamePositionUpdatePayload, buildCasinoGameUpdatePayload } from '../../helpers/casino';
import { createEditColumn } from '../../helpers/columns';
import { useCasinoGamesTable } from '../../hooks/useCasinoGamesTable';
import useMutateData from '../../hooks/useMutateData';
import usePermissions from '../../hooks/usePermissions';
import { createColumn } from '../../utils';
import PositionInput from '../atoms/PositionInput';
import TableTemplate from '../templates/TableTemplate';

const CasinoGamesPage = () => {
  const { isSuperAdmin } = usePermissions();
  const {
    data: casinoGamesData,
    isFetching,
    isLoading,
    updateQueryParams,
    changeQuery,
    handleSearch,
    handleSort,
    paginationModel,
  } = useCasinoGamesTable({
    defaultSort: {
      sortColumn: 'name',
      sortOrder: 'asc',
    },
  });
  const { updateDataAsync } = useMutateData(API_ROUTES.CASINO.GAME_UPDATE, [QUERY_KEYS.casinoGames], 'casino');

  const handlePositionChange = useCallback(
    (game: CasinoGameRow, position: number | null) => {
      return updateDataAsync(
        game.id,
        buildCasinoGamePositionUpdatePayload({ position }),
        undefined,
        'Game updated successfully.'
      );
    },
    [updateDataAsync]
  );

  const handleProviderGamePositionChange = useCallback(
    (game: CasinoGameRow, providerGamePosition: number | null) => {
      return updateDataAsync(
        game.id,
        buildCasinoGameUpdatePayload({ providerGamePosition }),
        undefined,
        'Game updated successfully.'
      );
    },
    [updateDataAsync]
  );

  const columns = useMemo<GridColDef<CasinoGameRow>[]>(() => {
    const casinoGamesColumns: GridColDef<CasinoGameRow>[] = [
      createColumn('name', 'Name', {
        minWidth: 220,
        flex: 1,
      }),
      createColumn('position', 'ALL Position', {
        minWidth: 150,
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams<CasinoGameRow>) => (
          <PositionInput value={row.position} onCommit={(position) => handlePositionChange(row, position)} />
        ),
      }),
      createColumn('providerGamePosition', 'Provider Position', {
        minWidth: 190,
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams<CasinoGameRow>) => (
          <PositionInput
            value={row.providerGamePosition}
            onCommit={(providerGamePosition) => handleProviderGamePositionChange(row, providerGamePosition)}
          />
        ),
      }),
      createColumn('aggregatorName', 'Aggregator', {
        minWidth: 160,
      }),
      createColumn('providerName', 'Provider', {
        minWidth: 180,
      }),
      createColumn('gameType', 'Game Type', {
        minWidth: 140,
      }),
      createColumn('categoryName', 'Category', {
        minWidth: 180,
      }),
    ];

    if (isSuperAdmin) {
      casinoGamesColumns.push(createEditColumn<CasinoGameRow>());
    }

    return casinoGamesColumns;
  }, [handlePositionChange, handleProviderGamePositionChange, isSuperAdmin]);

  return (
    <TableTemplate
      rows={casinoGamesData?.items || []}
      columns={columns}
      loading={isFetching || isLoading}
      rowCount={casinoGamesData?.count || 0}
      handlePaginationModelChange={updateQueryParams}
      handleSearch={handleSearch}
      handleSort={handleSort}
      changeQuery={changeQuery}
      paginationModel={paginationModel}
      filterType='casino-games'
    />
  );
};

export default CasinoGamesPage;
